import { useEffect, useState } from 'react';
import {
  ChartScopeInput,
  ChartBreakdownInput,
  GetChartDocument,
  GetChartFrequencyDocument,
  GetChartFrequencyQueryResult,
  GetChartFrequencyQueryVariables,
  GetChartQueryResult,
  GetChartQueryVariables,
} from '../generated/graphql';
import { IChart as LiftReportChart } from '../entity/Chart';
import GlobalPeriod from '../entity/GlobalPeriod';
import { useQuery } from '@apollo/client';
import { ChartBreakdownRequest, RequestVariables } from '@kaizenplatform/kaizen-chartjs';

const useKaizenChart = (liftReportChartSetting: GlobalPeriod, goalId: string | null, liftReport?: LiftReportChart) => {
  const [chartVariables, setChartVariables] = useState<GetChartQueryVariables>();
  const [frequencyVariables, setFrequencyVariables] = useState<GetChartQueryVariables>();

  const scope: ChartScopeInput = {
    from: liftReportChartSetting.from.toISO(),
    to: liftReportChartSetting.to.toISO(),
  };

  const { data: frequencyData, loading: frequencyLoading } = useQuery<
    GetChartFrequencyQueryResult,
    GetChartFrequencyQueryVariables
  >(GetChartFrequencyDocument, {
    skip: !liftReport || !frequencyVariables,
    fetchPolicy: 'no-cache',
    variables: frequencyVariables,
    context: {
      clientName: 'dataplatform',
    },
  });

  const initialFrequency =
    frequencyData && (frequencyData as any).chart?.frequencies
      ? (frequencyData as any).chart?.frequencies.includes('WEEKLY')
        ? 'WEEKLY'
        : (frequencyData as any).chart?.frequencies[0]
      : undefined;

  useEffect(() => {
    if (liftReport) {
      const scopeForChart = {
        from: liftReportChartSetting.from.toISO(),
        to: liftReportChartSetting.to.toISO(),
        frequency: initialFrequency,
      };

      const breakdowns = [];

      if (goalId) {
        breakdowns?.push(
          new ChartBreakdownRequest({
            breakdown: 'goal_id',
            filter: [goalId],
          }),
        );
      }

      const variablesForChart: GetChartQueryVariables = {
        id: liftReport ? liftReport.id : '',
        scope: scopeForChart,
        breakdowns: breakdowns,
      };

      setChartVariables(variablesForChart);
    }
  }, [frequencyData]);

  useEffect(() => {
    const scopeForChart = {
      from: liftReportChartSetting.from.toISO(),
      to: liftReportChartSetting.to.toISO(),
      frequency: chartVariables?.scope.frequency,
    };

    const newBreakdowns = (chartVariables?.breakdowns as Array<ChartBreakdownInput>)?.map((breakdown) => {
      if (breakdown.breakdown === 'goal_id') {
        breakdown.filter = goalId ? [goalId] : [];
      }

      return breakdown;
    });

    const variablesForChart: GetChartQueryVariables = {
      id: chartVariables ? chartVariables.id : '',
      scope: scopeForChart,
      breakdowns: newBreakdowns,
    };

    setChartVariables(variablesForChart);
  }, [goalId]);

  useEffect(() => {
    // 初期読み込み時の処理
    if (liftReport) {
      const scopeForChart = {
        from: liftReportChartSetting.from.toISO(),
        to: liftReportChartSetting.to.toISO(),
        frequency: initialFrequency,
      };

      const breakdowns = [];

      if (goalId) {
        breakdowns?.push(
          new ChartBreakdownRequest({
            breakdown: 'goal_id',
            filter: [goalId],
          }),
        );
      }

      const variablesForFrequency = {
        id: liftReport ? liftReport.id : '',
        scope: scope,
      };

      setFrequencyVariables(variablesForFrequency);

      const variablesForChart: GetChartQueryVariables = {
        id: liftReport ? liftReport.id : '',
        scope: scopeForChart,
        breakdowns: breakdowns,
      };

      setChartVariables(variablesForChart);
    }
  }, []);

  const {
    data: chartData,
    variables: variables,
    loading: chartLoading,
    // error: chartError,
    refetch: refetchChart,
  } = useQuery<GetChartQueryResult, GetChartQueryVariables>(GetChartDocument, {
    skip: !liftReport || !frequencyData || !chartVariables || !chartVariables.id,
    fetchPolicy: 'no-cache',
    variables: {
      ...chartVariables,
    } as GetChartQueryVariables,
    notifyOnNetworkStatusChange: true,
    context: {
      clientName: 'dataplatform',
    },
  });

  useEffect(() => {
    setChartVariables(variables);
  }, [variables]);

  useEffect(() => {
    const scopeForChart = {
      from: liftReportChartSetting.from.toISO(),
      to: liftReportChartSetting.to.toISO(),
      frequency: chartVariables?.scope.frequency,
    };

    const variablesForChart: GetChartQueryVariables = {
      id: chartVariables ? chartVariables.id : '',
      scope: scopeForChart,
      breakdowns: chartVariables?.breakdowns,
    };

    setChartVariables(variablesForChart);
  }, [liftReportChartSetting]);

  const refetch = (variables: RequestVariables) => {
    const _variables = { ...variables };
    if (goalId && _variables.breakdowns) {
      _variables.breakdowns.push(
        new ChartBreakdownRequest({
          breakdown: 'goal_id',
          filter: [goalId],
        }),
      );
    }

    refetchChart(RequestVariables.fromJSON(_variables));
  };

  return { chartData, frequencyData, initialFrequency, frequencyLoading, chartLoading, refetch };
};

export default useKaizenChart;
